/**
 * @component ry-placeholder
 * @category  feedback
 * @summary   Skeleton loading state (Fomantic UI `.ui.placeholder` equivalent). Renders muted, animated shapes that approximate the layout of forthcoming content — a perceived-perf shim shown while data loads. Configure via `shape="lines|image|header|paragraph"` for canned compositions, OR author rows manually inside.
 *
 * Reference: Fomantic UI's placeholder element. ry-placeholder picks the
 * common Fomantic compositions and exposes them via one attribute. For
 * pixel-exact skeletons, author rows inside `<ry-placeholder>` directly —
 * the wrapper supplies the shimmer animation, you supply the shape.
 *
 * @attribute shape     'lines' | 'image' | 'header' | 'paragraph'?   Pre-built composition. `lines` renders N text lines (configurable via [lines]); `image` renders an image rectangle + a few caption lines; `header` renders a header bar + 2 lines; `paragraph` renders 4 lines. Omit for a bare wrapper that ONLY supplies the shimmer; provide your own children as rows.
 * @attribute lines     number?   default=3   Number of text rows rendered when `shape="lines"`. Ignored for other shapes.
 * @attribute fluid     boolean   default=false   Stretch the placeholder to fill its container.
 * @attribute inverted  boolean   default=false   Renders light skeleton bars on a dark background. Pure CSS hook.
 *
 * @content   When `shape` is set, children are REPLACED by the canned markup at setup. When `shape` is absent, your children are preserved — each direct child is tagged as a row and inherits the shimmer animation.
 *
 * @dom (shape="lines")
 *   ry-placeholder[shape="lines"][lines]   role="status"   aria-label="Loading content"
 *     > div.ry-placeholder__row    [data-ry-target="row"]   (repeated N times)
 *
 * @dom (shape="image")
 *   ry-placeholder[shape="image"]   role="status"   aria-label="Loading content"
 *     > div.ry-placeholder__image  [data-ry-target="image"]
 *     > div.ry-placeholder__row    [data-ry-target="row"]   (repeated for caption)
 *     > div.ry-placeholder__row    [data-ry-target="row"]
 *
 * @gotcha  `[shape]` REPLACES innerHTML at setup. Author your own skeleton rows ONLY when no shape is set — mixing the two loses your authored content.
 *
 * @gotcha  `[lines]` is read once at setup. Updating it post-mount does NOT add or remove rows — replace the element to change the line count, or set the lines attribute before mount.
 *
 * @gotcha  ARIA `role="status"` + `aria-label="Loading content"` are applied unconditionally so screen readers announce the placeholder. Consumers that need silence (e.g. when the placeholder is purely visual filler) should remove the role manually after setup OR set their own `aria-label` before mount.
 *
 * @gotcha  This is purely visual — there is no `[active]` toggle. Show by adding to DOM; hide by removing. Use a parent class or v-if-style swap to manage visibility.
 *
 * @example bare-wrapper-with-authored-rows
 * ```html
 * <ry-placeholder>
 *   <div>Custom row 1</div>
 *   <div style="width: 70%">Shorter row</div>
 *   <div>Final row</div>
 * </ry-placeholder>
 * ```
 *
 * @example shape-lines
 * ```html
 * <ry-placeholder shape="lines" lines="5"></ry-placeholder>
 * ```
 *
 * @example shape-image
 * ```html
 * <ry-placeholder shape="image"></ry-placeholder>
 * ```
 *
 * @example shape-paragraph
 * ```html
 * <ry-placeholder shape="paragraph"></ry-placeholder>
 * ```
 *
 * @example shape-header
 * ```html
 * <ry-placeholder shape="header"></ry-placeholder>
 * ```
 *
 * @example fluid
 * ```html
 * <ry-placeholder shape="paragraph" fluid></ry-placeholder>
 * ```
 *
 * @tutorial mount-sets-status-role-and-label
 * ```js
 * const el = mount('<ry-placeholder></ry-placeholder>');
 * await expectAttr(el, 'role', 'status', 'host gets role="status"');
 * await expectAttr(el, 'aria-label', 'Loading content', 'auto aria-label applied');
 * ```
 *
 * @tutorial shape-lines-renders-default-3-rows
 * ```js
 * const el = mount('<ry-placeholder shape="lines"></ry-placeholder>');
 * const rows = el.querySelectorAll('[data-ry-target="row"]');
 * await expectEqual(rows.length, 3, 'default lines is 3');
 * ```
 *
 * @tutorial shape-lines-honors-lines-attribute
 * ```js
 * const el = mount('<ry-placeholder shape="lines" lines="7"></ry-placeholder>');
 * const rows = el.querySelectorAll('[data-ry-target="row"]');
 * await expectEqual(rows.length, 7, 'lines attr controls row count');
 * ```
 *
 * @tutorial shape-image-renders-image-plus-rows
 * ```js
 * const el = mount('<ry-placeholder shape="image"></ry-placeholder>');
 * await expectEqual(!!el.querySelector('[data-ry-target="image"]'), true, 'image rectangle present');
 * const rows = el.querySelectorAll('[data-ry-target="row"]');
 * await expectEqual(rows.length, 2, 'image variant has 2 caption rows');
 * ```
 *
 * @tutorial shape-header-renders-header-plus-rows
 * ```js
 * const el = mount('<ry-placeholder shape="header"></ry-placeholder>');
 * await expectEqual(!!el.querySelector('[data-ry-target="header"]'), true, 'header bar present');
 * const rows = el.querySelectorAll('[data-ry-target="row"]');
 * await expectEqual(rows.length, 2, 'header variant has 2 body rows');
 * ```
 *
 * @tutorial shape-paragraph-renders-4-rows
 * ```js
 * const el = mount('<ry-placeholder shape="paragraph"></ry-placeholder>');
 * const rows = el.querySelectorAll('[data-ry-target="row"]');
 * await expectEqual(rows.length, 4, 'paragraph variant has 4 rows');
 * ```
 *
 * @tutorial no-shape-preserves-children-as-rows
 * ```js
 * // Wrong assumption: ry-placeholder always replaces innerHTML. Without
 * // [shape], authored children are preserved and tagged as rows so they
 * // inherit the shimmer.
 * const el = mount(`
 *   <ry-placeholder>
 *     <div>Custom A</div>
 *     <div>Custom B</div>
 *   </ry-placeholder>
 * `);
 * const rows = el.querySelectorAll('[data-ry-target="row"]');
 * await expectEqual(rows.length, 2, 'two authored rows preserved');
 * await expectEqual(rows[0].textContent, 'Custom A', 'content preserved');
 * ```
 *
 * @tutorial preserves-explicit-aria-label
 * ```js
 * const el = mount('<ry-placeholder aria-label="Custom"></ry-placeholder>');
 * await expectAttr(el, 'aria-label', 'Custom', 'explicit aria-label not overridden');
 * ```
 *
 * @see ry-loader
 * @see ry-progress
 */

import { RyElement } from '../core/ry-element.js';

type Shape = 'lines' | 'image' | 'header' | 'paragraph';

export class RyPlaceholder extends RyElement {
  setup(): void {
    this.#applyAria();
    const shape = this.getAttribute('shape') as Shape | null;
    if (shape) {
      this.#renderShape(shape);
    } else {
      this.#tagAuthoredChildren();
    }
  }

  #applyAria(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'status');
    if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', 'Loading content');
  }

  #renderShape(shape: Shape): void {
    const linesAttr = parseInt(this.getAttribute('lines') ?? '', 10);
    const rowMarkup = `<div data-ry-target="row" class="ry-placeholder__row"></div>`;

    if (shape === 'lines') {
      const count = Number.isFinite(linesAttr) && linesAttr > 0 ? linesAttr : 3;
      this.innerHTML = rowMarkup.repeat(count);
      return;
    }
    if (shape === 'image') {
      this.innerHTML = `
        <div data-ry-target="image" class="ry-placeholder__image"></div>
        ${rowMarkup}
        ${rowMarkup}
      `;
      return;
    }
    if (shape === 'header') {
      this.innerHTML = `
        <div data-ry-target="header" class="ry-placeholder__header"></div>
        ${rowMarkup}
        ${rowMarkup}
      `;
      return;
    }
    if (shape === 'paragraph') {
      this.innerHTML = rowMarkup.repeat(4);
      return;
    }
  }

  #tagAuthoredChildren(): void {
    Array.from(this.children).forEach((child) => {
      const el = child as HTMLElement;
      if (!el.hasAttribute('data-ry-target')) el.setAttribute('data-ry-target', 'row');
      if (!el.classList.contains('ry-placeholder__row')) el.classList.add('ry-placeholder__row');
    });
  }
}

customElements.define('ry-placeholder', RyPlaceholder);
